// TrackedItemRow primitive.
//
// The shared row component for row-based deal-flow workflows. Row chrome
// (title + optional code chip + description + status chip + actions)
// matches the analyst criteria-checklist rows. Every row exposes a
// "View Details" action that opens the popup modal, a state-change
// control, and — when permitted and non-terminal — an Escalate action.
//
// The optional judgment plugin layers on the analyst-certification
// controls; the current consumers (Intake Checklist, Document Requests)
// leave it undefined.

import { useState } from "react";
import { FileText } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import {
  TrackedItemEscalationChip,
  TrackedItemStatusChip,
} from "./TrackedItemStatusChip";
import {
  TrackedItemEscalationAction,
  TrackedItemModal,
  TrackedItemStateControl,
} from "./TrackedItemModal";
import { TrackedItemEscalateModal } from "./TrackedItemEscalateModal";
import { resolveStateDef } from "./types";
import type { TrackedItemRowProps } from "./types";

export function TrackedItemRow({
  title,
  code,
  description,
  requiredEvidence,
  escalationPath,
  metadata,
  detailExtra,
  state,
  stateSet,
  onStateChange,
  stateChangePending = false,
  notes,
  onNotesChange,
  notesSavePending = false,
  canEscalate,
  escalationStatus,
  escalationDirective,
  escalationResolvedAt,
  onEscalate,
  escalatePending = false,
  judgmentPlugin,
  isReadOnly = false,
}: TrackedItemRowProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);

  const def = resolveStateDef(stateSet, state);
  // Escalation is offered when permitted, the item is non-terminal and not
  // already escalated, and no EscalationRecord is currently live.
  const escalateAvailable =
    canEscalate &&
    !isReadOnly &&
    !def.isTerminal &&
    state !== "Escalated" &&
    escalationStatus === "None";

  const handleEscalate = async (reason: string) => {
    await onEscalate(reason);
    setEscalateOpen(false);
  };

  return (
    <li
      className={`border border-border-light rounded-md bg-white-pure px-space-3 py-space-2 ${
        isReadOnly ? "opacity-70" : ""
      }`}
      data-testid="tracked-item-row"
      data-state={state}
    >
      <div className="flex items-start gap-space-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-space-2 mb-space-1">
            {code && (
              <span className="font-display text-label uppercase tracking-label text-gold-shadow">
                {code}
              </span>
            )}
            <TrackedItemStatusChip state={state} stateSet={stateSet} />
            {escalationStatus !== "None" && (
              <TrackedItemEscalationChip status={escalationStatus} />
            )}
          </div>
          <div className="text-small text-onyx font-medium truncate" title={title}>
            {title}
          </div>
          {description && (
            <p className="text-caption text-gray-mid m-0 mt-space-1 line-clamp-2">
              {description}
            </p>
          )}
          {notes.trim() && (
            <p className="text-caption text-gray-mid m-0 mt-space-1 italic truncate">
              Notes: {notes}
            </p>
          )}
        </div>

        <div className="flex items-center gap-space-1 shrink-0">
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setDetailOpen(true)}
            title="View item details"
          >
            <FileText size={14} aria-hidden />
            View Details
          </Button>
          {!judgmentPlugin && (
            <TrackedItemStateControl
              state={state}
              stateSet={stateSet}
              onStateChange={onStateChange}
              pending={stateChangePending}
              disabled={isReadOnly || def.isTerminal}
            />
          )}
          {!judgmentPlugin && (
            <TrackedItemEscalationAction
              state={state}
              stateSet={stateSet}
              escalationStatus={escalationStatus}
              escalationDirective={escalationDirective}
              escalationResolvedAt={escalationResolvedAt}
              onStateChange={onStateChange}
              pending={stateChangePending}
              disabled={isReadOnly}
            />
          )}
          {escalateAvailable && (
            <Button
              variant="secondary"
              size="sm"
              disabled={escalatePending}
              onClick={() => setEscalateOpen(true)}
            >
              Escalate
            </Button>
          )}
        </div>
      </div>

      <TrackedItemModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={title}
        code={code}
        description={description}
        requiredEvidence={requiredEvidence}
        escalationPath={escalationPath}
        metadata={metadata}
        detailExtra={detailExtra}
        state={state}
        stateSet={stateSet}
        onStateChange={onStateChange}
        stateChangePending={stateChangePending}
        notes={notes}
        onNotesChange={onNotesChange}
        notesSavePending={notesSavePending}
        canEscalate={escalateAvailable}
        escalationStatus={escalationStatus}
        escalationDirective={escalationDirective}
        escalationResolvedAt={escalationResolvedAt}
        onEscalateClick={() => {
          setDetailOpen(false);
          setEscalateOpen(true);
        }}
        judgmentPlugin={judgmentPlugin}
        isReadOnly={isReadOnly}
      />
      <TrackedItemEscalateModal
        open={escalateOpen}
        onClose={() => setEscalateOpen(false)}
        itemTitle={title}
        escalationPath={escalationPath}
        onConfirm={handleEscalate}
        pending={escalatePending}
      />
    </li>
  );
}
